import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  Chip,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IosShareIcon from "@mui/icons-material/IosShare";
import HardwareRoundedIcon from "@mui/icons-material/HardwareRounded";
import HardwareOutlinedIcon from "@mui/icons-material/HardwareOutlined";
import ReplyIcon from "@mui/icons-material/Reply";
import color from "../../Constants/colors";
import { QuestionItem } from "../../Models/question";
import { useNavigate } from "react-router-dom";
import { useCurrentWorkspace } from "../../Query/useWorkspace";
import { useMyself } from "../../Query/useUsers";
import { Features, UserRole } from "../../Enums/companyStatus";
import { useCertifications } from "../../Query/useCertifications";
import PolicyIcon from "@mui/icons-material/Policy";
import theme from "../../Constants/theme";
import { useTranslation } from "react-i18next";
import { hasPermission, hasRole } from "../../Helpers/authHelpers";
import { ComoButton } from "../Common/ComoButton";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

interface QuestionListTableProps {
  data: QuestionItem[];
  isLoading?: boolean;
  sortField: string;
  sortOrder: "asc" | "desc";
  handleSort: (field: keyof QuestionItem) => void;
  onDelete: (id: number) => void;
  onEdit: (item: QuestionItem) => void;
  onCopy: (item: QuestionItem) => void;
  onToggleArchive: (id: number, archived: boolean) => void;
  onExport: (id: number, name: string) => void;
  onSetDefault: (item: QuestionItem) => void;
  onShare: (id: number) => void;
}

const QuestionListTable: React.FC<QuestionListTableProps> = ({
  data,
  isLoading,
  sortField,
  sortOrder,
  handleSort,
  onDelete,
  onEdit,
  onCopy,
  onToggleArchive,
  onExport,
  onSetDefault,
  onShare,
}) => {
  const navigate = useNavigate();
  const { colors: workspace } = useWorkspaceColors();
  const { data: currentWorkspace } = useCurrentWorkspace();
  const { data: myself } = useMyself();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<QuestionItem | null>(null);
  const { data: certifications } = useCertifications();
  const digitalPerformanceCertifications = certifications?.filter(
    (cert) => cert.digital_performance === true,
  );
  const workspaceCertifications =
    currentWorkspace?.workspace_certifications ?? null;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation("myQuestionLists");

  const handleShareClick = (item: QuestionItem) => {
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const handleConfirmShare = () => {
    if (selectedItem) {
      onShare(selectedItem.id);
      setOpenDialog(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <>
      <Box sx={{ position: "relative" }}>
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1,
            }}
          >
            <CircularProgress sx={{ color: workspace.primary_dark }} />
          </Box>
        )}
        <Table
          sx={{
            backgroundColor: color.white,
            opacity: isLoading ? 0.5 : 1,
            transition: "opacity 0.2s ease-in-out",
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: color.white, height: "36px" }}>
              <TableCell
                sx={{
                  color: workspace.primary_dark,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                  padding: "6px 8px",
                  borderBottom: `1px solid ${color.gray200}`,
                }}
                onClick={() => handleSort("name")}
              >
                {t("myQuestionLists.table.columnName")}
                {sortField === "name" && (
                  <TableSortLabel active direction={sortOrder} />
                )}
              </TableCell>
              <TableCell
                sx={{
                  color: workspace.primary_dark,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                  padding: "6px 8px",
                  borderBottom: `1px solid ${color.gray200}`,
                }}
                onClick={() => handleSort("category")}
              >
                {t("myQuestionLists.table.columnCategory")}
                {sortField === "category" && (
                  <TableSortLabel active direction={sortOrder} />
                )}
              </TableCell>
              <TableCell
                sx={{
                  color: workspace.primary_dark,
                  fontWeight: 600,
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                  padding: "6px 8px",
                  borderBottom: `1px solid ${color.gray200}`,
                }}
              >
                {t("myQuestionLists.table.columnActions")}
              </TableCell>
              <TableCell
                sx={{
                  color: workspace.primary_dark,
                  fontWeight: 600,
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                  padding: "6px 8px",
                  borderBottom: `1px solid ${color.gray200}`,
                }}
              >
                {t("myQuestionLists.table.columnStatus")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow
                key={item.id}
                sx={{
                  backgroundColor: color.white,
                  "&:hover": { backgroundColor: color.gray100 },
                  borderBottom: `1px solid ${color.gray200}`,
                  height: "40px",
                }}
              >
                <TableCell
                  sx={{
                    color: workspace.primary_dark,
                    fontWeight: item.default ? 600 : 400,
                    cursor: "pointer",
                    borderBottom: `1px solid ${color.gray200}`,
                    padding: "6px 8px",
                    fontSize: "0.8rem",
                  }}
                  onClick={() => navigate(`/question_list/${item.id}`)}
                >
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography
                        variant={isMobile ? "body2" : "body1"}
                        sx={{
                          color: workspace.primary_dark,
                          fontSize: isMobile ? "0.75rem" : "0.85rem",
                        }}
                      >
                        {item.name}
                      </Typography>
                      {item.default && (
                        <Tooltip
                          title={t("myQuestionLists.table.tooltipDefault")}
                        >
                          <HardwareRoundedIcon
                            sx={{
                              fontSize: "0.9rem",
                              color: workspace.accent,
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: color.gray500,
                        fontSize: "0.7rem",
                        marginTop: "2px",
                      }}
                    >
                      {t("myQuestionLists.table.createdBy")}{" "}
                      {item.default
                        ? "System"
                        : item.created_by?.name || "System"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    color: color.gray700,
                    borderBottom: `1px solid ${color.gray200}`,
                    padding: "6px 8px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      label={item.category.name}
                      size="small"
                      sx={{
                        backgroundColor: workspace.primary_dark,
                        color: color.white,
                        fontWeight: 500,
                        fontSize: "0.7rem",
                        height: "20px",
                      }}
                    />
                    {digitalPerformanceCertifications?.some(
                      (cert) =>
                        cert.id === item.category.id &&
                        cert.digital_performance === true,
                    ) && (
                      <Tooltip title="Digital Performance">
                        <PolicyIcon
                          fontSize="small"
                          sx={{
                            color: workspace.primary_dark,
                            fontSize: "1rem",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: `1px solid ${color.gray200}`,
                    padding: "2px 4px",
                  }}
                >
                  <Tooltip
                    title={
                      item.default || item.in_use
                        ? t("myQuestionLists.table.tooltipCannotEdit")
                        : t("myQuestionLists.table.tooltipEdit")
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        sx={{
                          color: workspace.primary_dark,
                          opacity: item.default || item.in_use ? 0.4 : 1,
                          "&:hover": { bgcolor: color.gray100 },
                          "&:disabled": {
                            opacity: 0.5,
                            color: workspace.primary_dark,
                          },
                          padding: "4px",
                          fontSize: "1rem",
                        }}
                        onClick={() => onEdit(item)}
                        disabled={item.default || item.in_use}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      item.default || item.in_use
                        ? t("myQuestionLists.table.tooltipCannotDelete")
                        : t("myQuestionLists.table.tooltipDelete")
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        sx={{
                          color: color.red600,
                          opacity: item.default || item.in_use ? 0.4 : 1,
                          "&:hover": { bgcolor: color.red50 },
                          "&:disabled": {
                            opacity: 0.5,
                            color: workspace.primary_dark,
                          },
                          padding: "4px",
                          fontSize: "1rem",
                        }}
                        onClick={() => onDelete(item.id)}
                        disabled={item.default || item.in_use}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("myQuestionLists.table.tooltipCopy")}>
                    <span>
                      <IconButton
                        size="small"
                        sx={{
                          color: workspace.primary_dark,
                          "&:hover": { bgcolor: color.gray100 },
                          "&:disabled": {
                            opacity: 0.5,
                            color: workspace.primary_dark,
                          },
                          padding: "4px",
                          fontSize: "1rem",
                        }}
                        onClick={() => onCopy(item)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      item.archived
                        ? t("myQuestionLists.table.tooltipUnarchive")
                        : t("myQuestionLists.table.tooltipArchive")
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onToggleArchive(item.id, item.archived)}
                        sx={{
                          color: workspace.primary_dark,
                          opacity: item.default ? 0.4 : 1,
                          "&:hover": { bgcolor: color.gray100 },
                          "&:disabled": {
                            opacity: 0.5,
                            color: workspace.primary_dark,
                          },
                          padding: "4px",
                          fontSize: "1rem",
                        }}
                        disabled={item.default}
                      >
                        {item.archived ? (
                          <UnarchiveIcon fontSize="small" />
                        ) : (
                          <ArchiveIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  {hasRole(UserRole.ADMIN) && (
                    <>
                      <Tooltip
                        title={
                          item.default
                            ? t("myQuestionLists.table.tooltipAlreadyDefault")
                            : t("myQuestionLists.table.tooltipSetDefault")
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onSetDefault(item)}
                            sx={{
                              color: workspace.accent,
                              opacity: item.default || item.archived ? 0.4 : 1,
                              padding: "4px",
                              fontSize: "1rem",
                            }}
                            disabled={item.default || item.archived}
                          >
                            {item.default ? (
                              <HardwareRoundedIcon fontSize="small" />
                            ) : (
                              <HardwareOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                  {(hasRole(UserRole.ADMIN) ||
                    hasPermission(Features.EVALUATION)) && (
                    <Tooltip title={t("myQuestionLists.table.tooltipExport")}>
                      <span>
                        <IconButton
                          size="small"
                          sx={{
                            color: workspace.primary_dark,
                            "&:hover": { bgcolor: color.gray100 },
                            "&:disabled": {
                              opacity: 0.5,
                              color: workspace.primary_dark,
                            },
                            padding: "4px",
                            fontSize: "1rem",
                          }}
                          onClick={() => onExport(item.id, item.name)}
                        >
                          <IosShareIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {item.created_by?.id === myself?.id &&
                    currentWorkspace?.workspace !== undefined &&
                    item.default === false &&
                    item.workspace === null &&
                    workspaceCertifications?.some(
                      (cert: { certification_category: number }) =>
                        cert.certification_category === item.category.id,
                    ) && (
                      <Tooltip title={t("myQuestionLists.table.tooltipShare")}>
                        <span>
                          <IconButton
                            size="small"
                            sx={{
                              color: workspace.primary_dark,
                              "&:hover": { bgcolor: color.gray100 },
                              "&:disabled": {
                                opacity: 0.5,
                                color: workspace.primary_dark,
                              },
                              padding: "4px",
                              fontSize: "1rem",
                            }}
                            onClick={() => handleShareClick(item)}
                          >
                            <ReplyIcon
                              sx={{ transform: "scaleX(-1)", fontSize: "1rem" }}
                              fontSize="small"
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                </TableCell>
                <TableCell
                  sx={{
                    color: color.gray700,
                    borderBottom: `1px solid ${color.gray200}`,
                    padding: "6px 8px",
                  }}
                >
                  <Chip
                    label={
                      item.archived
                        ? t("myQuestionLists.table.archived")
                        : t("myQuestionLists.table.active")
                    }
                    color={item.archived ? "error" : "success"}
                    size="small"
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.7rem",
                      height: "20px",
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
          },
        }}
      >
        <DialogTitle sx={{ color: workspace.primary_dark, fontWeight: 600 }}>
          {t("myQuestionLists.dialogs.shareDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: color.gray700 }}>
            {t("myQuestionLists.dialogs.shareDialogMessage")}
          </Typography>
          <Typography
            sx={{
              color: color.gray500,
              fontStyle: "italic",
              fontSize: "0.8rem",
              mt: 1,
            }}
          >
            {t("myQuestionLists.dialogs.shareDialogSubtext")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <ComoButton onClick={handleCloseDialog} secondary>
            {t("myQuestionLists.dialogs.shareCancel")}
          </ComoButton>
          <ComoButton onClick={handleConfirmShare}>
            {t("myQuestionLists.dialogs.shareConfirm")}
          </ComoButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuestionListTable;
